"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  CreditCardIcon,
  InfoIcon,
  AlertTriangleIcon,
  Building2Icon,
  CalendarIcon,
  GaugeIcon,
  UserIcon,
} from "lucide-react";
import { NonVwfsLessorField } from "@/components/backoffice/cat/vehicle-assignment/funding/non-vwfs-lessor-field";
import { useVehicleAssignmentStepError } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-error";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";
import { saveDraftData } from "@/app/lib/actions/cat/funding-step";
import {
  getVwfsLessorMdmNumber,
  getVwfsLessorSiret,
} from "@/lib/cat/vwfs-lessor-siret";
import {
  type FundingFormLeaseAnswer,
  mapDraftToFundingFormState,
} from "@/lib/schemas/cat/funding-draft";
import type {
  FundingType,
  VwfsType,
  OtherLeasingType,
} from "@/prisma/generated/client";

type SaveFundingDraftParams = Parameters<typeof saveDraftData>[0];

interface fundingProps {
  onCanProceedChange?: (canProceed: boolean) => void;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
}

export function RetailFundingStep({
  onCanProceedChange,
  onBeforeNextChange,
}: fundingProps) {
  const { showStepError } = useVehicleAssignmentStepError();
  const currentDraft = useCurrentDraftStore((state) => state.currentDraft);
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );

  const fundingFormSeed = useMemo(
    () => mapDraftToFundingFormState(currentDraft),
    [currentDraft],
  );

  const [isLeased, setIsLeased] = useState(fundingFormSeed.isLeased);
  const [fundingType, setFundingType] = useState<FundingType | null>(
    fundingFormSeed.fundingType,
  );
  const [vwfsType, setVwfsType] = useState<VwfsType | null>(
    fundingFormSeed.vwfsType,
  );
  const [otherLeasingType, setOtherLeasingType] =
    useState<OtherLeasingType | null>(fundingFormSeed.otherLeasingType);
  const [businessNumber, setBusinessNumber] = useState(
    fundingFormSeed.businessNumber,
  );
  const [lessorSiret, setLessorSiret] = useState(fundingFormSeed.lessorSiret);
  const [lessorMdmNumber, setLessorMdmNumber] = useState(
    fundingFormSeed.lessorMdmNumber,
  );
  const [contractDuration, setContractDuration] = useState(
    fundingFormSeed.contractDuration,
  );
  const [maxKm, setMaxKm] = useState(fundingFormSeed.maxKm);
  const [siretError, setSiretError] = useState<string>("");
  const [driverFirstName, setDriverFirstName] = useState(
    fundingFormSeed.driverFirstName,
  );
  const [driverLastName, setDriverLastName] = useState(
    fundingFormSeed.driverLastName,
  );

  const validForm = useCallback((): boolean => {
    if (!isLeased) return false;

    if (isLeased === "no") {
      return !!fundingType;
    }

    // Leased vehicle
    if (!fundingType) return false;

    if (fundingType === "VWFS") {
      if (!vwfsType || !businessNumber) return false;
    } else if (fundingType === "OTHER") {
      if (
        !otherLeasingType ||
        !lessorSiret ||
        !lessorMdmNumber ||
        !businessNumber
      ) {
        return false;
      }
      if (siretError) return false;
    }

    // Duration and max km required for all lease types
    if (!contractDuration || !maxKm) return false;

    return true;
  }, [
    isLeased,
    fundingType,
    vwfsType,
    otherLeasingType,
    businessNumber,
    lessorSiret,
    lessorMdmNumber,
    contractDuration,
    maxKm,
    siretError,
  ]);

  useEffect(() => {
    onCanProceedChange?.(validForm());
  }, [
    isLeased,
    fundingType,
    vwfsType,
    otherLeasingType,
    businessNumber,
    lessorSiret,
    lessorMdmNumber,
    contractDuration,
    maxKm,
    siretError,
    validForm,
    onCanProceedChange,
  ]);

  const handleBeforeNext = useCallback(async () => {
    if (currentDraft === null) {
      showStepError("Aucun brouillon associé à cette affectation.");
      return false;
    }

    const params: SaveFundingDraftParams = {
      id: currentDraft.id,
      isLeased: isLeased === "yes",
      fundingType: fundingType as FundingType,
      vwfsType: fundingType === "VWFS" ? vwfsType : null,
      otherLeasingType: fundingType === "OTHER" ? otherLeasingType : null,
      businessNumber: isLeased === "yes" ? businessNumber : null,
      lessorSiret: fundingType === "OTHER" ? lessorSiret : null,
      lessorMdmNumber: fundingType === "OTHER" ? lessorMdmNumber : null,
      contractDuration: isLeased === "yes" ? contractDuration : null,
      maxKm: isLeased === "yes" ? maxKm : null,
      driverFirstName,
      driverLastName,
    };

    const result = await saveDraftData(params);

    if (!result.success) {
      showStepError(
        result.message ??
          "Une erreur est survenue lors de l'enregistrement du véhicule.",
      );
      return false;
    }

    if (!result.data) {
      showStepError(
        "Aucune donnée client n'a été retournée après l'enregistrement.",
      );
      return false;
    }

    setCurrentDraft(result.data);

    return true;
  }, [
    businessNumber,
    contractDuration,
    currentDraft,
    driverFirstName,
    driverLastName,
    fundingType,
    isLeased,
    lessorSiret,
    lessorMdmNumber,
    maxKm,
    otherLeasingType,
    setCurrentDraft,
    showStepError,
    vwfsType,
  ]);

  useEffect(() => {
    onBeforeNextChange?.(handleBeforeNext);
    return () => onBeforeNextChange?.(null);
  }, [handleBeforeNext, onBeforeNextChange]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-3 pb-2">
          <CardTitle className="flex items-center space-x-2">
            <CreditCardIcon className="h-5 w-5 text-[#005EA8]" />
            <span>Location & Financement</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-2">
          {/* Initial required question */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-sm font-medium">
                Le véhicule est-il loué ? *
              </Label>
            </div>
            <RadioGroup
              className="flex flex-wrap gap-4"
              value={isLeased}
              onValueChange={(value) => {
                setIsLeased(value as FundingFormLeaseAnswer);
                setFundingType(null);
                setVwfsType(null);
                setOtherLeasingType(null);
                setBusinessNumber("");
                setLessorSiret("");
                setContractDuration("");
                setMaxKm("");
                setSiretError("");
              }}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="leased-yes" />
                <Label
                  htmlFor="leased-yes"
                  className="cursor-pointer font-medium"
                >
                  Oui
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="leased-no" />
                <Label
                  htmlFor="leased-no"
                  className="cursor-pointer font-medium"
                >
                  Non
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Case 1: vehicle not leased */}
          {isLeased === "no" && (
            <div className="space-y-4 rounded-lg bg-blue-50 p-4">
              <Label className="text-base font-semibold">
                Type de financement *
              </Label>
              <RadioGroup
                value={fundingType}
                onValueChange={(value) => {
                  setFundingType(value as FundingType);
                }}
              >
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value={"CREDIT"} id="credit" />
                    <Label
                      htmlFor="credit"
                      className="cursor-pointer font-medium"
                    >
                      Crédit classique
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value={"CASH"} id="cash" />
                    <Label
                      htmlFor="cash"
                      className="cursor-pointer font-medium"
                    >
                      Comptant
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Case 2: leased vehicle */}
          {isLeased === "yes" && (
            <div className="space-y-6 rounded-lg p-4">
              <div className="space-y-4">
                <Label className="text-base font-semibold">
                  Type de financement *
                </Label>
                <RadioGroup
                  value={fundingType}
                  onValueChange={(value) => {
                    setFundingType(value as FundingType);
                    setVwfsType(null);
                    setOtherLeasingType(null);
                    setBusinessNumber("");
                    setLessorSiret("");
                    setSiretError("");
                  }}
                >
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="VWFS" id="vwfs" />
                      <Label
                        htmlFor="vwfs"
                        className="cursor-pointer font-medium"
                      >
                        Financement VWFS
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="OTHER" id="other" />
                      <Label
                        htmlFor="other"
                        className="cursor-pointer font-medium"
                      >
                        Financement autre que VWFS
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* VWFS financing */}
              {fundingType === "VWFS" && (
                <div className="space-y-4 rounded-lg border-2 p-4">
                  <Alert>
                    <InfoIcon className="h-4 w-4" />
                    <AlertDescription>
                      Le SIRET VWFS sera automatiquement déduit selon votre
                      choix de type de financement.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label>Type de financement VWFS *</Label>
                    <Select
                      value={vwfsType as string}
                      onValueChange={(value) => {
                        setVwfsType(value as VwfsType);
                        setLessorMdmNumber(
                          getVwfsLessorMdmNumber(value as VwfsType),
                        );
                        setLessorSiret(getVwfsLessorSiret(value as VwfsType));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner le type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LOUN">LOUN</SelectItem>
                        <SelectItem value="LLDFI">LLDFI</SelectItem>
                        <SelectItem value="LOA">LOA</SelectItem>
                        <SelectItem value="FINANCE_LEASE">
                          Crédit bail
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {vwfsType && (
                    <>
                      <div className="space-y-2">
                        <Label className="flex items-center space-x-2">
                          <Building2Icon className="h-4 w-4" />
                          <span>SIRET VWFS (auto-déterminé)</span>
                        </Label>
                        <Input
                          value={lessorSiret}
                          disabled
                          className="bg-gray-100"
                        />
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label>{"Numéro de bon de commande loueur *"}</Label>
                    <Input
                      placeholder="25565445"
                      value={businessNumber}
                      onChange={(e) => setBusinessNumber(e.target.value)}
                    />
                    <Alert>
                      <InfoIcon className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        <strong>Importance métier:</strong>
                        {
                          "Ce numéro permet d'identifier de manière unique le contrat de financement VWFS et assure la traçabilité dans tous les systèmes Volkswagen."
                        }
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              )}

              {/* b) Non-VWFS financing */}
              {fundingType === "OTHER" && (
                <div className="space-y-4 rounded-lg border-2 p-4">
                  <Alert>
                    <InfoIcon className="h-4 w-4" />
                    <AlertDescription>
                      Le SIRET saisi ne doit pas correspondre à un SIRET VWFS.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label>Type de location *</Label>
                    <Select
                      value={otherLeasingType as string}
                      onValueChange={(value) => {
                        setOtherLeasingType(value as OtherLeasingType);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner le type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LLD">
                          {"LLD (Location Longue Durée)"}
                        </SelectItem>
                        <SelectItem value="LOA">
                          {"LOA (Location avec Option d'Achat)"}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <NonVwfsLessorField
                    fundingType={fundingType}
                    siret={lessorSiret}
                    mdmNumber={lessorMdmNumber}
                    onSiretChange={setLessorSiret}
                    onMdmNumberChange={setLessorMdmNumber}
                    validationError={siretError}
                    onValidationErrorChange={setSiretError}
                  />

                  <div className="space-y-2">
                    <Label>Numéro de bon de commande loueur *</Label>
                    <Input
                      placeholder="25565445"
                      value={businessNumber}
                      onChange={(e) => setBusinessNumber(e.target.value)}
                    />
                    <Alert>
                      <InfoIcon className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        <strong>Importance métier:</strong>
                        {
                          "Ce numéro permet d'identifier de manière unique le contrat de financement et assure la traçabilité dans tous les systèmes Volkswagen."
                        }
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              )}

              {/* Required fields for all lease types */}
              {(fundingType === "VWFS" || fundingType === "OTHER") && (
                <div className="space-y-4 rounded-lg p-4">
                  <div className="mb-2 flex items-center space-x-2">
                    <InfoIcon className="h-5 w-5 text-yellow-700" />
                    <Label className="text-base font-semibold text-yellow-900">
                      Données du contrat
                    </Label>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4" />
                        <span>Durée du contrat (mois) *</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="Ex: 36"
                        value={contractDuration}
                        onChange={(e) => setContractDuration(e.target.value)}
                        min="1"
                        max="120"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <GaugeIcon className="h-4 w-4" />
                        <span>Kilomètres maximum *</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="Ex: 45000"
                        value={maxKm}
                        onChange={(e) => setMaxKm(e.target.value)}
                        min="1000"
                        step="1000"
                      />
                    </div>
                  </div>

                  {(Number.parseInt(contractDuration) > 96 ||
                    Number.parseInt(maxKm) < 5000) && (
                    <Alert>
                      <AlertTriangleIcon className="h-4 w-4" />
                      <AlertDescription className="text-sm">
                        Attention: Valeurs inhabituelles détectées. Veuillez
                        vérifier les données saisies.
                      </AlertDescription>
                    </Alert>
                  )}

                  <Label className="flex items-center space-x-2">
                    <UserIcon className="h-4 w-4" />
                    <span>Informations conducteur (facultatives) </span>
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <span>Prénom</span>
                      </Label>
                      <Input
                        value={driverFirstName}
                        onChange={(e) => setDriverFirstName(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <span>Nom</span>
                      </Label>
                      <Input
                        value={driverLastName}
                        onChange={(e) => setDriverLastName(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Selection summary */}
          {validForm() && (
            <div className="rounded-lg bg-blue-50 p-4">
              <h4 className="mb-3 font-semibold text-gray-900">
                Résumé de la sélection
              </h4>
              <div className="flex flex-wrap gap-2">
                {isLeased === "yes" ? (
                  <>
                    <Badge className="bg-green-600 text-white">
                      Véhicule loué
                    </Badge>
                    {fundingType === "VWFS" && vwfsType && (
                      <Badge className="bg-[#005EA8] text-white">
                        VWFS - {vwfsType}
                      </Badge>
                    )}
                    {fundingType === "OTHER" && otherLeasingType && (
                      <Badge className="bg-orange-600 text-white">
                        Autre loueur - {otherLeasingType}
                      </Badge>
                    )}
                    {contractDuration && (
                      <Badge variant="outline">{contractDuration} mois</Badge>
                    )}
                    {maxKm && (
                      <Badge variant="outline">
                        {Number.parseInt(maxKm).toLocaleString()} km
                      </Badge>
                    )}
                  </>
                ) : (
                  <>
                    <Badge className="">Véhicule non loué</Badge>
                    {fundingType === "CREDIT" && (
                      <Badge variant="outline">Crédit classique</Badge>
                    )}
                    {fundingType === "CASH" && (
                      <Badge variant="outline">Comptant</Badge>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
